import {
  DASHBOARD_AGGREGATION,
  DASHBOARD_APPROVAL_MODE,
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';

export const DASHBOARD_VALUE_TYPE_OPTIONS = Object.freeze([
  { value: DASHBOARD_VALUE_TYPE.PERCENTAGE, label: 'Percentage' },
  { value: DASHBOARD_VALUE_TYPE.INTEGER, label: 'Whole number' },
  { value: DASHBOARD_VALUE_TYPE.DECIMAL, label: 'Decimal number' }
]);

export const DASHBOARD_AGGREGATION_OPTIONS = Object.freeze([
  { value: DASHBOARD_AGGREGATION.AVERAGE, label: 'Average' },
  { value: DASHBOARD_AGGREGATION.SUM, label: 'Sum' },
  { value: DASHBOARD_AGGREGATION.LATEST, label: 'Latest value' },
  { value: DASHBOARD_AGGREGATION.MINIMUM, label: 'Minimum' },
  { value: DASHBOARD_AGGREGATION.MAXIMUM, label: 'Maximum' }
]);

export const DASHBOARD_APPROVAL_OPTIONS = Object.freeze([
  {
    value: DASHBOARD_APPROVAL_MODE.REQUIRED,
    label: 'Approval required'
  },
  {
    value: DASHBOARD_APPROVAL_MODE.NOT_REQUIRED,
    label: 'No approval required'
  }
]);

export const EMPTY_DASHBOARD_METRIC_FORM = Object.freeze({
  metricKey: '',
  label: '',
  description: '',
  valueType: DASHBOARD_VALUE_TYPE.DECIMAL,
  unitLabel: '',
  decimalPlaces: '0',
  minimum: '',
  maximum: '',
  isRequired: false,
  aggregation: DASHBOARD_AGGREGATION.AVERAGE,
  approvalMode: DASHBOARD_APPROVAL_MODE.REQUIRED,
  colour: '',
  isFeatured: false
});

const valueTypeValues = DASHBOARD_VALUE_TYPE_OPTIONS.map(({ value }) => value);
const aggregationValues = DASHBOARD_AGGREGATION_OPTIONS.map(
  ({ value }) => value
);
const approvalValues = DASHBOARD_APPROVAL_OPTIONS.map(({ value }) => value);

export const createDashboardMetricKey = (label = '') => {
  const normalized = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const prefixed = /^[a-z]/.test(normalized)
    ? normalized
    : normalized
      ? `metric_${normalized}`
      : '';
  return prefixed.slice(0, 64);
};

export const dashboardMetricToForm = (metric) => ({
  ...(metric?.fieldSource ? { fieldSource: metric.fieldSource } : {}),
  metricKey: metric?.metricKey ?? '',
  label: metric?.label ?? '',
  description: metric?.description ?? '',
  valueType: metric?.valueType ?? DASHBOARD_VALUE_TYPE.DECIMAL,
  unitLabel: metric?.unitLabel ?? '',
  decimalPlaces: String(metric?.decimalPlaces ?? 0),
  minimum:
    metric?.minimum === null || metric?.minimum === undefined
      ? ''
      : String(metric.minimum),
  maximum:
    metric?.maximum === null || metric?.maximum === undefined
      ? ''
      : String(metric.maximum),
  isRequired: Boolean(metric?.isRequired),
  aggregation: metric?.aggregation ?? DASHBOARD_AGGREGATION.AVERAGE,
  approvalMode: metric?.approvalMode ?? DASHBOARD_APPROVAL_MODE.REQUIRED,
  colour: metric?.colour ?? '',
  isFeatured: Boolean(metric?.isFeatured)
});

export const changeDashboardMetricValueType = (form, valueType) => ({
  ...form,
  valueType,
  ...(valueType !== DASHBOARD_VALUE_TYPE.PERCENTAGE &&
  form.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE &&
  form.unitLabel === '%'
    ? { unitLabel: '' }
    : {}),
  ...(valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE
    ? {
        unitLabel: '%',
        aggregation:
          form.aggregation === DASHBOARD_AGGREGATION.SUM
            ? DASHBOARD_AGGREGATION.AVERAGE
            : form.aggregation
      }
    : {}),
  ...(valueType === DASHBOARD_VALUE_TYPE.INTEGER ? { decimalPlaces: '0' } : {})
});

const parseLimit = (field, value, errors) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    errors[field] =
      `${field === 'minimum' ? 'Minimum' : 'Maximum'} must be a number.`;
    return null;
  }
  return parsed;
};

const hasConfiguredPrecision = (value, decimalPlaces) => {
  const scaled = value * 10 ** decimalPlaces;
  return Math.abs(scaled - Math.round(scaled)) < 1e-9;
};

export const validateDashboardMetric = (
  form,
  { activeFeaturedMetrics = 0, metricWillBeActive = true } = {}
) => {
  // Linked formatting comes from the field on every read; do not persist a second configuration.
  if (form?.fieldSource) form = { ...form, valueType: 'decimal', unitLabel: '', decimalPlaces: '0', minimum: '', maximum: '' };
  const errors = {};
  if (
    form?.fieldSource &&
    !/^[0-9a-f-]{36}$/i.test(form.fieldSource.fieldId || '')
  )
    errors.fieldSource = 'Select a numeric layout field.';
  const metricKey =
    typeof form?.metricKey === 'string' ? form.metricKey.trim() : '';
  const label = typeof form?.label === 'string' ? form.label.trim() : '';
  const description =
    typeof form?.description === 'string' ? form.description.trim() : '';
  const unitLabel =
    typeof form?.unitLabel === 'string' ? form.unitLabel.trim() : '';
  const colour = typeof form?.colour === 'string' ? form.colour.trim() : '';

  if (!/^[a-z][a-z0-9_]{1,63}$/.test(metricKey)) {
    errors.metricKey =
      'Metric key must be 2–64 lowercase characters, start with a letter, and use only letters, numbers, or underscores.';
  }
  if (!label) {
    errors.label = 'Metric label is required.';
  } else if (label.length > 100) {
    errors.label = 'Metric label cannot exceed 100 characters.';
  }
  if (description.length > 500) {
    errors.description = 'Description cannot exceed 500 characters.';
  }
  if (unitLabel.length > 30) {
    errors.unitLabel = 'Unit label cannot exceed 30 characters.';
  }
  if (!valueTypeValues.includes(form?.valueType)) {
    errors.valueType = 'Select a supported value type.';
  }
  if (!aggregationValues.includes(form?.aggregation)) {
    errors.aggregation = 'Select a supported aggregation.';
  }
  if (!approvalValues.includes(form?.approvalMode)) {
    errors.approvalMode = 'Select an approval policy.';
  }

  const decimalPlaces = Number(form?.decimalPlaces);
  if (
    !Number.isInteger(decimalPlaces) ||
    decimalPlaces < 0 ||
    decimalPlaces > 6
  ) {
    errors.decimalPlaces = 'Decimal places must be a whole number from 0 to 6.';
  }
  if (form?.valueType === DASHBOARD_VALUE_TYPE.INTEGER && decimalPlaces !== 0) {
    errors.decimalPlaces = 'Whole-number metrics must use zero decimal places.';
  }
  if (
    form?.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE &&
    unitLabel !== '%'
  ) {
    errors.unitLabel = 'Percentage metrics must use % as the unit label.';
  }
  if (
    form?.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE &&
    form?.aggregation === DASHBOARD_AGGREGATION.SUM
  ) {
    errors.aggregation = 'Percentage metrics cannot use sum aggregation.';
  }
  if (colour && !/^#[0-9A-Fa-f]{6}$/.test(colour)) {
    errors.colour = 'Colour must use a six-digit hex value such as #11A5A4.';
  }

  const minimum = parseLimit('minimum', form?.minimum, errors);
  const maximum = parseLimit('maximum', form?.maximum, errors);
  if (minimum !== null && maximum !== null && minimum > maximum) {
    errors.minimum = 'Minimum cannot be greater than maximum.';
  }
  if (
    Number.isInteger(decimalPlaces) &&
    decimalPlaces >= 0 &&
    decimalPlaces <= 6
  ) {
    if (minimum !== null && !hasConfiguredPrecision(minimum, decimalPlaces)) {
      errors.minimum = `Minimum cannot have more than ${decimalPlaces} decimal places.`;
    }
    if (maximum !== null && !hasConfiguredPrecision(maximum, decimalPlaces)) {
      errors.maximum = `Maximum cannot have more than ${decimalPlaces} decimal places.`;
    }
  }
  if (form?.isFeatured && metricWillBeActive && activeFeaturedMetrics >= 5) {
    errors.isFeatured =
      'Only five active metrics can be displayed as top widgets.';
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    values: {
      ...(form?.fieldSource ? { fieldSource: form.fieldSource } : {}),
      metricKey,
      label,
      description: description || null,
      valueType: form?.valueType,
      unitLabel: unitLabel || null,
      decimalPlaces,
      minimum,
      maximum,
      isRequired: Boolean(form?.isRequired),
      aggregation: form?.aggregation,
      approvalMode: form?.approvalMode,
      colour: colour ? colour.toUpperCase() : null,
      isFeatured: Boolean(form?.isFeatured)
    }
  };
};

export const sortDashboardMetrics = (metrics = []) =>
  [...metrics].sort(
    (left, right) =>
      left.displayOrder - right.displayOrder ||
      left.label.localeCompare(right.label)
  );

export const moveDashboardMetric = (metrics, metricDefinitionId, direction) => {
  const ordered = sortDashboardMetrics(metrics);
  const currentIndex = ordered.findIndex(
    (metric) => metric.metricDefinitionId === metricDefinitionId
  );
  const targetIndex = currentIndex + direction;
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return ordered.map((metric) => metric.metricDefinitionId);
  }

  const [metric] = ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, metric);
  return ordered.map((item) => item.metricDefinitionId);
};

export const getActiveFeaturedMetricCount = (
  metrics = [],
  excludedMetricDefinitionId = null
) =>
  metrics.filter(
    (metric) =>
      metric.metricDefinitionId !== excludedMetricDefinitionId &&
      metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE &&
      metric.isFeatured
  ).length;
